// Size-sensitive module: it sits in the splitChunks cluster documented under CLUSTER NOTE in
// config/webpack/splitChunksBudget.ts. Read that before growing this file, comments included.
import { mergeQueryParamsToUrl } from '@shared/utils/urlHelpers';
import { useCallback, useMemo } from 'react';
import { useQueryParamHelper } from '@shared/core/queryString';

export enum TRANSFERRED_STAYS_QUERY_PARAMS {
  discountCode = 'discountCode',
  // Share Your Stay reward coupon, carried by the link in the email a sharer gets when their
  // referral pays out. Retained here rather than threaded per call site because every builder in
  // `hotelRoutes.ts` composes its params from a fixed shape and would otherwise drop it on the
  // first hop — search → hotel page — long before checkout, which is the only place it is read.
  joyReferralRewardCode = 'joyReferralRewardCode',
  // Share Your Stay *recipient* code, put on the link by the guest who shared their booking. Same
  // fixed-shape problem as the reward code above, and the only two places a share link can land are
  // the hotel page and the search page — so every recipient reaches checkout across at least one
  // `hotelRoutes.ts` hop. Without retention here the code dies on that hop and the guest books with
  // no code at all, losing both the discount their friend's link promised and the friend's reward.
  joyReferralCode = 'joyReferralCode',
  // Per-event $0 attribution promotion; `useReservation` sends it, so a hop that drops it charges full price.
  weddingCode = 'weddingCode',
}

interface UseRetainSupportedStaysParams {
  retainSupportedStaysParams: (targetUrl: string) => string;
}

/**
 * A custom hook that retains supported query parameters (e.g., `discountCode`)
 * from the current URL and merges them into any target URL.
 *
 * @returns {UseRetainSupportedStaysParams} An object containing a function to enhance URLs with retained query parameters.
 */
export const useRetainSupportedStaysParams = (): UseRetainSupportedStaysParams => {
  const queryParamHelper = useQueryParamHelper();
  const discountCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.discountCode);
  const joyReferralRewardCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.joyReferralRewardCode);
  const joyReferralCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.joyReferralCode);
  const weddingCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.weddingCode);

  // Stable across renders on purpose: callers hold this in `useMemo`/`useCallback` dependency lists
  // (`hotelRoutes.ts` puts it in both `buildUrlWithPreservedUtm` and the route-map memo below it),
  // and a fresh function every render had been quietly defeating those.
  const retainSupportedStaysParams = useCallback(
    (targetUrl: string): string => mergeQueryParamsToUrl(targetUrl, { discountCode, joyReferralRewardCode, joyReferralCode, weddingCode }),
    [discountCode, joyReferralRewardCode, joyReferralCode, weddingCode],
  );

  return useMemo(() => ({ retainSupportedStaysParams }), [retainSupportedStaysParams]);
};
